import { z } from "zod";

// =============
// Admin Email DLQ Schemas
// =============

/**
 * Error category for email failures.
 */
export const EmailDlqErrorCategorySchema = z.enum([
	"retryable",
	"terminal",
	"unknown",
]);
export type EmailDlqErrorCategory = z.infer<typeof EmailDlqErrorCategorySchema>;

/**
 * Admin email DLQ record schema.
 * Matches the R2 stored JSON structure.
 */
export const AdminEmailDlqRecordSchema = z.object({
	id: z.string(),
	attempt: z.number(),
	createdAt: z.string(),
	unixMs: z.number(),
	recipientType: z.enum(["partner", "user"]),
	recipientId: z.string(),
	emailType: z.string(),
	provider: z.literal("postmark"),
	error: z.object({
		category: EmailDlqErrorCategorySchema,
		message: z.string(),
		raw: z.record(z.string(), z.unknown()),
	}),
	payload: z.object({
		to: z.array(z.string()),
		from: z.string(),
		subject: z.string(),
		html: z.string().nullable(),
		text: z.string().nullable(),
		headers: z.record(z.string(), z.string()),
		rawMime: z.string(),
	}),
});
export type AdminEmailDlqRecord = z.infer<typeof AdminEmailDlqRecordSchema>;

/**
 * Email DLQ list item (includes R2 key).
 */
export const AdminEmailDlqListItemSchema = z.object({
	key: z.string(),
	record: AdminEmailDlqRecordSchema,
});
export type AdminEmailDlqListItem = z.infer<typeof AdminEmailDlqListItemSchema>;

/**
 * Admin email DLQ list input with filters.
 */
export const AdminEmailDlqListInputSchema = z.object({
	recipientType: z.enum(["partner", "user"]).optional(),
	date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.optional(),
	limit: z.coerce.number().min(1).max(100).default(50),
	cursor: z.string().optional(),
});
export type AdminEmailDlqListInput = z.infer<
	typeof AdminEmailDlqListInputSchema
>;

/**
 * Admin email DLQ list output.
 */
export const AdminEmailDlqListOutputSchema = z.object({
	records: z.array(AdminEmailDlqListItemSchema),
	truncated: z.boolean(),
	cursor: z.string().optional(),
});
export type AdminEmailDlqListOutput = z.infer<
	typeof AdminEmailDlqListOutputSchema
>;

/**
 * Admin email DLQ replay input.
 */
export const AdminEmailDlqReplayInputSchema = z.object({
	key: z.string(),
});
export type AdminEmailDlqReplayInput = z.infer<
	typeof AdminEmailDlqReplayInputSchema
>;

/**
 * Admin email DLQ replay output.
 */
export const AdminEmailDlqReplayOutputSchema = z.object({
	success: z.boolean(),
	replayKey: z.string(),
});
export type AdminEmailDlqReplayOutput = z.infer<
	typeof AdminEmailDlqReplayOutputSchema
>;
